import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { useStytchWorkspaceMembers } from '@/hooks/useStytchWorkspaceMembers'
import { zodResolver } from '@hookform/resolvers/zod'
import { useStytchOrganization } from '@stytch/react/b2b'
import { Role } from 'honeygrid-types'
import {
	WORKSPACE_EDITOR_ROLE,
	WORKSPACE_OWNER_ROLE,
	WORKSPACE_VIEWER_ROLE,
} from 'honeygrid-types/validators-and-types/StytchWorkspaceTypes'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import type { FC } from 'react'

interface InviteUserProps {
	isOpen: boolean
	onCancel: () => void
	onSubmit: (emailInput: string, roleInput: Role) => void
}

const formSchema = z.object({
	email: z.email(),
	name: z.string().min(1, 'Name is required'),
	role: z.enum(Role),
})

export const InviteUser: FC<InviteUserProps> = ({ isOpen, onSubmit, onCancel }) => {
	const { organization } = useStytchOrganization()
	const { inviteMember } = useStytchWorkspaceMembers()

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		mode: 'onSubmit',
		reValidateMode: 'onBlur',
		defaultValues: {
			email: '',
			name: '',
			role: Role.Editor,
		},
	})

	/**
	 * Maps honeygrid Role to Stytch workspace role
	 */
	const mapRoleToStytchRole = (role: Role): string => {
		switch (role) {
			case Role.Owner:
				return WORKSPACE_OWNER_ROLE
			case Role.Editor:
				return WORKSPACE_EDITOR_ROLE
			case Role.Viewer:
				return WORKSPACE_VIEWER_ROLE
			default:
				return WORKSPACE_EDITOR_ROLE
		}
	}

	const handleSubmit = async (values: z.infer<typeof formSchema>) => {
		if (!organization?.organization_id) {
			toast.error('No workspace selected')
			return
		}

		try {
			// Wait for the mutation to complete before closing dialog and showing success
			await inviteMember({
				email_address: values.email,
				name: values.name,
				roles: [mapRoleToStytchRole(values.role)],
			})

			form.reset()
			onSubmit(values.email, values.role)
			toast.success('User invited', {
				description: `Successfully invited ${values.name} as ${values.role}`,
			})
		} catch (error) {
			console.error(error)
			toast.error('Failed to invite user')
		}
	}

	const workspaceRoles = useMemo(() => Object.keys(Role) as Array<keyof typeof Role>, [])

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Invite user to {organization?.organization_name || 'workspace'}</DialogTitle>
					<DialogDescription>
						Enter the email address and role for the user you want to invite.
					</DialogDescription>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<label htmlFor="name" className="text-sm font-medium text-foreground">
										Name
									</label>
									<FormControl>
										<Input placeholder="Enter full name" {...field} autoFocus />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem>
									<label htmlFor="email" className="text-sm font-medium text-foreground">
										Email
									</label>
									<FormControl>
										<Input placeholder="Enter email address" type="email" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="role"
							render={({ field }) => (
								<FormItem>
									<label htmlFor="role" className="text-sm font-medium text-foreground">
										Role
									</label>
									<Select onValueChange={field.onChange} defaultValue={field.value}>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder="Select a role" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{workspaceRoles.map((role) => (
												<SelectItem key={role} value={Role[role]}>
													{role}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
						<DialogFooter>
							<Button type="submit" disabled={!form.formState.isValid}>
								Invite
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
